/**
 * Insights hub — panoramica Analytics con KPI live e card di navigazione
 * verso le sotto-pagine (fatturato mensile, statistiche, quality, reporting,
 * previsione carico, MaeWorld per SuperAdmin).
 *
 * I KPI riusano le stesse queryKey delle pagine di dettaglio
 * (['monthly-summary'], ['yearly-trend'], ['statistics', '12m']): la cache
 * react-query è condivisa, quindi entrare nel dettaglio non rifà le chiamate.
 */
import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Avatar,
  Card,
  Group,
  NumberFormatter,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { Sparkline } from '@mantine/charts';
import {
  IconBolt,
  IconBuildingFactory2,
  IconChartBar,
  IconChevronRight,
  IconClock,
  IconHelp,
  IconReceipt2,
  IconReport,
  IconTrendingUp,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import { httpRequest } from '@/shared/api/client';
import { fetchList } from '@/shared/api/jsonapi/client';
import { PageSkeleton } from '@/shared/components/PageSkeleton';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/insights')({ component: InsightsLayout });

// ── Tipi response, allineati alle pagine di dettaglio ──

/** Shape di /api/insights/statistics (vedi features/insights/Statistics.tsx). */
interface StatsResponse {
  totalEmployees: number;
  activeProjects: number;
  hoursThisMonth: number;
  billableHoursThisMonth: number;
  monthlyTrend: Array<{ month: string; hours: number; billable: number }>;
}

/** Shape di /api/invoices/monthly-summary (vedi insights.monthly-invoices.tsx). */
interface Summary {
  totals: {
    billingHours: number;
    ordinaryHours: number;
    overtimeHours: number;
    projectsCount: number;
    billableProjects: number;
  };
}

/** Shape di /api/invoices/yearly-trend (vedi insights.monthly-invoices.tsx). */
interface Trend {
  series: Array<{ label: string; billingHours: number }>;
}

interface Ticket extends Denormalized {
  type: 'tickets';
  status?: string;
}

/** Layout: a /insights mostra l'hub, sui figli (es. /insights/monthly-invoices) l'Outlet. */
function InsightsLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isChild = path.replace(/\/+$/, '') !== '/insights';
  if (isChild) return <Outlet />;
  return <InsightsHub />;
}

function InsightsHub() {
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();

  const now = dayjs();
  const year = now.year();
  const month = now.month() + 1;

  // ── KPI: query già usate altrove (stesse queryKey ⇒ cache condivisa) ──
  const summaryQ = useQuery({
    queryKey: ['monthly-summary', year, month],
    queryFn: () => httpRequest<Summary>(`/api/invoices/monthly-summary?year=${year}&month=${month}`, { endpointType: 'plain' }),
    staleTime: 5 * 60_000,
  });
  const statsQ = useQuery({
    queryKey: ['statistics', '12m'],
    queryFn: () => httpRequest<StatsResponse>('/api/insights/statistics?range=12m', { endpointType: 'plain' }),
    staleTime: 5 * 60_000,
  });
  const ticketsQ = useQuery({
    queryKey: ['insights-open-tickets'],
    queryFn: () => fetchList<Ticket>('tickets', {
      filter: { status: { eq: 'open' } },
      page: { size: 100 },
    }),
    staleTime: 60_000,
  });
  // Trend 12 mesi solo per la sparkline della card "Fatturato mensile".
  const trendQ = useQuery({
    queryKey: ['yearly-trend'],
    queryFn: () => httpRequest<Trend>('/api/invoices/yearly-trend?monthsBack=12', { endpointType: 'plain' }),
    staleTime: 10 * 60_000,
  });

  const kpiLoading = summaryQ.isLoading || statsQ.isLoading || ticketsQ.isLoading;
  const allFailed = summaryQ.isError && statsQ.isError && ticketsQ.isError;

  const totals = summaryQ.data?.totals;
  const stats = statsQ.data;
  const openTickets = ticketsQ.data?.total ?? null;
  const billableRate = stats && stats.hoursThisMonth > 0
    ? (stats.billableHoursThisMonth / stats.hoursThisMonth) * 100
    : null;

  const invoiceSpark = (trendQ.data?.series ?? []).map((p) => p.billingHours);
  const hoursSpark = (stats?.monthlyTrend ?? []).map((m) => m.hours);

  return (
    <Stack gap="lg">
      <div>
        <Title order={1}>
          <IconChartBar size={26} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Insights
        </Title>
        <Text c="dimmed" size="sm">
          Panoramica analytics: KPI di {now.format('MMMM YYYY')} e accesso rapido a statistiche e report.
        </Text>
      </div>

      {/* KPI LIVE */}
      {allFailed ? (
        <QueryErrorAlert
          error={statsQ.error ?? summaryQ.error ?? ticketsQ.error}
          onRetry={() => {
            void summaryQ.refetch();
            void statsQ.refetch();
            void ticketsQ.refetch();
          }}
        />
      ) : kpiLoading ? (
        <PageSkeleton variant="cards" count={5} />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing="md">
          <KpiCard
            icon={<IconReceipt2 size={24} />}
            label="Ore fatturabili (mese)"
            value={totals
              ? <NumberFormatter value={totals.billingHours} decimalScale={1} suffix="h" />
              : '—'}
            subline={totals
              ? `${totals.projectsCount} commesse · ${totals.billableProjects} fatturabili`
              : 'Dato non disponibile'}
            color="teal"
            onClick={() => void navigate({ to: '/insights/monthly-invoices' })}
          />
          <KpiCard
            icon={<IconBuildingFactory2 size={24} />}
            label="Progetti attivi"
            value={stats ? stats.activeProjects : '—'}
            subline={stats ? undefined : 'Dato non disponibile'}
            color="green"
            onClick={() => void navigate({ to: '/projects' })}
          />
          <KpiCard
            icon={<IconClock size={24} />}
            label="Ore mese"
            value={stats ? <NumberFormatter value={stats.hoursThisMonth} decimalScale={0} suffix="h" /> : '—'}
            subline={billableRate != null ? `${billableRate.toFixed(0)}% fatturabili` : 'Dato non disponibile'}
            color="orange"
            onClick={() => void navigate({ to: '/statistics' })}
          />
          <KpiCard
            icon={<IconUsers size={24} />}
            label="Dipendenti"
            value={stats ? stats.totalEmployees : '—'}
            subline={stats ? undefined : 'Dato non disponibile'}
            color="blue"
            onClick={() => void navigate({ to: '/workforce' })}
          />
          <KpiCard
            icon={<IconHelp size={24} />}
            label="Ticket aperti"
            value={openTickets ?? '—'}
            subline={openTickets == null ? 'Dato non disponibile' : undefined}
            color={openTickets != null && openTickets > 0 ? 'orange' : 'gray'}
            onClick={() => void navigate({ to: '/tickets' })}
          />
        </SimpleGrid>
      )}

      {/* NAVIGAZIONE SOTTO-PAGINE */}
      <div>
        <Text fw={600} mb="sm">Esplora</Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          <NavCard
            to="/insights/monthly-invoices"
            icon={IconReceipt2}
            color="teal"
            title="Fatturato mensile"
            description="Ore fatturabili per commessa, totali mese e trend degli ultimi 12 mesi."
            spark={invoiceSpark}
          />
          <NavCard
            to="/statistics"
            icon={IconChartBar}
            color="blue"
            title="Statistiche"
            description="Trend operativo: ore, fatturabilità, assenze e smart working con confronto periodo."
            spark={hoursSpark}
          />
          <NavCard
            to="/quality"
            icon={IconBolt}
            color="grape"
            title="Quality"
            description="On-time delivery, accuratezza stime e quality score, personali e di team."
          />
          <NavCard
            to="/reporting"
            icon={IconReport}
            color="cyan"
            title="Reporting"
            description="Report con export CSV/XLS/PDF, preset salvabili e cronologia degli export."
          />
          <NavCard
            to="/project-forecast"
            icon={IconTrendingUp}
            color="orange"
            title="Previsione carico"
            description="Saturazione settimanale per commessa: capacità vs allocato e settimane critiche."
          />
          {isSuperAdmin && (
            <NavCard
              to="/maeworld"
              icon={IconWorld}
              color="indigo"
              title="MaeWorld"
              description="Dashboard cross-tenant per SuperAdmin: attività, ore e progetti per tenant."
            />
          )}
        </SimpleGrid>
      </div>
    </Stack>
  );
}

/** Card KPI compatta, stessa estetica delle StatCard della home. */
function KpiCard({ icon, label, value, subline, color, onClick }: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  subline?: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <Card withBorder padding="md" radius="md" style={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" c="dimmed">{label}</Text>
          <Text fz={26} fw={700} c={color === 'gray' ? undefined : `${color}.7`}>{value}</Text>
          {subline && <Text size="xs" c="dimmed" lineClamp={1}>{subline}</Text>}
        </div>
        <Avatar variant="light" color={color} radius="md" size={40}>{icon}</Avatar>
      </Group>
    </Card>
  );
}

/** Card di navigazione verso una sotto-pagina, con mini sparkline opzionale. */
function NavCard({ to, icon: Icon, color, title, description, spark }: {
  to: string;
  icon: typeof IconChartBar;
  color: string;
  title: string;
  description: string;
  spark?: number[];
}) {
  return (
    <Card
      component={Link}
      to={to}
      withBorder
      padding="lg"
      radius="md"
      style={{ color: 'inherit', textDecoration: 'none' }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group wrap="nowrap" align="flex-start" gap="md">
          <ThemeIcon size="xl" radius="md" variant="light" color={color}>
            <Icon size={26} />
          </ThemeIcon>
          <div>
            <Text fw={600}>{title}</Text>
            <Text size="sm" c="dimmed" lineClamp={2}>{description}</Text>
          </div>
        </Group>
        <IconChevronRight size={18} style={{ opacity: 0.5, flexShrink: 0, marginTop: 4 }} />
      </Group>
      {spark && spark.length > 1 && (
        <Sparkline
          w="100%"
          h={36}
          mt="sm"
          data={spark}
          curveType="natural"
          color={color}
          fillOpacity={0.35}
          strokeWidth={1.5}
        />
      )}
    </Card>
  );
}
